import "bootstrap/dist/css/bootstrap.min.css";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { ChangeEventHandler, useEffect, useMemo, useState } from "react";
import * as Feather from "react-feather";
import { useDispatch, useSelector } from "react-redux";
import Card from "../components/custom/Card";
import FormInput from "../components/custom/FormInput";
import Main from "../components/custom/Main";
import PageContainer from "../components/custom/PageContainer";
import Option from "../components/Option";
import { useToast } from "../components/toast/ToastProvider";
import api, { APIResponse, getAxiosRequestConfig } from "../config/api";
import {
  currencyFormat,
  LocalFormatDate,
  rupeeFormat,
  today,
  toYMD,
} from "../config/functions";
import {
  AccountHolderType,
  fetchAccountHolderAction,
} from "../redux/actions/accountHolder";
import { AuthUserType } from "../redux/actions/authUserActions";
import { fetchDropDown } from "../redux/actions/dropDownAction";
import { TransactionType } from "../redux/actions/transactionAction";
import { StoreState } from "../redux/reducers";
import { AppDispatch } from "../redux/store";

interface openBalance {
  openingBalance: String;
}
export interface DaybookTransactionType {
  sourceId: number;
  date: string;
  accountName: string;
  accountNumber: number;
  debitAmount: number;
  creditAmount: number;
  particulars: string;
}
interface AccountSummary {
  accountNumber: number;
  accountName: string;
  openingBalance: number;
  totalCredit: number;
  totalDebit: number;
  currentBalance: number;
}
interface TableBody {
  sno: number;
  // accountNumber: string
  // accountName: string
  // accountType: string
  date: string;
  particulars: string;
  accountName: string;
  income: string;
  expense: string;
  balance?: string;
  openingBalance?: string;
}

const DayBookReport = () => {
  const toast = useToast();
  const dispatch = useDispatch<AppDispatch>();

  const authUser = useSelector<StoreState, AuthUserType>(
    (state) => state.authUser,
  );
  const token = authUser.token!;
  const config = getAxiosRequestConfig(token);

  const accountHolder = useSelector<StoreState, AccountHolderType[]>(
    (state) => state.account,
  );

  // const [date, setDate] = useState<string>(today())
  const [fromDate, setFromDate] = useState<string>(today());
  const [accountNumber, setAccountNumber] = useState<string>("");
  const [accountName, setAccountName] = useState<string>("");

  const [dayBookTransaction, setDayBookTransaction] = useState<
    AccountSummary[]
  >([]);
  const [data2, setData2] = useState<AccountSummary[]>([]);

  const [openingBalance, setOpeningBalance] = useState<string>("0");

  const [transactionData, setTransactionData] = useState<
    DaybookTransactionType[]
  >([]);

  console.log("trans", transactionData);

  const BankList = useMemo<AccountHolderType[]>(() => {
    return accountHolder.filter((ah) => {
      return ah.accountName?.toLowerCase()?.includes("bank");
    });
  }, [accountHolder]);

  console.log(accountHolder);
  console.log(BankList);

  const handleFromDate: ChangeEventHandler<HTMLInputElement> = (e) => {
    setFromDate(e.target.value);
  };

  const sortByDate = (ad: any, bd: any) => {
    let a1 = toYMD(ad.date);
    let b1 = toYMD(bd.date);

    let a = new Date(a1).getTime();
    let b = new Date(b1).getTime();

    return a - b;
  };

  const groupBy = (xs: any[], key: string) => {
    return xs.reduce((rv, x) => {
      (rv[x[key]] = rv[x[key]] || []).push(x);
      return rv;
    }, {});
  };

  const totalDebit = useMemo(() => {
    return dayBookTransaction.reduce((sum, item) => sum + item.totalDebit, 0);
  }, [dayBookTransaction]);

  const totalCredit = useMemo(() => {
    return dayBookTransaction.reduce((sum, item) => sum + item.totalCredit, 0);
  }, [dayBookTransaction]);

  const totalOpening = useMemo(() => {
    return dayBookTransaction.reduce(
      (sum, item) => sum + item.openingBalance,
      0,
    );
  }, [dayBookTransaction]);

  const totalBalance = useMemo(() => {
    return dayBookTransaction.reduce(
      (sum, item) => sum + item.currentBalance,
      0,
    );
  }, [dayBookTransaction]);

  const totalPartDebit = useMemo(() => {
    return transactionData.reduce((sum, item) => sum + item.debitAmount, 0);
  }, [transactionData]);

  const totalPartCredit = useMemo(() => {
    return transactionData.reduce((sum, item) => sum + item.creditAmount, 0);
  }, [transactionData]);

  const handleDownloadPDF = () => {
    const doc = new jsPDF("p", "mm", "a4");

    doc.setFontSize(18);
    doc.text(" Day Report: " + LocalFormatDate(fromDate), 70, 12);

    doc.setFontSize(12);
    doc.text("Summary: ", 15, 22);

    const totalPartDebit = transactionData.reduce(
      (sum, item) => sum + (Number(item.debitAmount) || 0),
      0,
    );

    const totalPartCredit = transactionData.reduce(
      (sum, item) => sum + (Number(item.creditAmount) || 0),
      0,
    );

    const totalDayDebit = dayBookTransaction.reduce(
      (sum, item) => sum + (Number(item.totalDebit) || 0),
      0,
    );
    const totalDayCredit = dayBookTransaction.reduce(
      (sum, item) => sum + (Number(item.totalCredit) || 0),
      0,
    );
    const totalOpeningBalance = dayBookTransaction.reduce(
      (sum, item) => sum + (Number(item.openingBalance) || 0),
      0,
    );
    const totalCurrentBalance = dayBookTransaction.reduce(
      (sum, item) => sum + (Number(item.currentBalance) || 0),
      0,
    );

    let y = 25;

    // ================= SUMMARY TABLE =================
    autoTable(doc, {
      theme: "grid",
      tableWidth: "auto",
      styles: {
        fontSize: 10,
        cellPadding: 2,
        overflow: "linebreak",
        textColor: [0, 0, 0],
        lineColor: 0,
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        fontSize: 10,
        lineColor: 0,
        lineWidth: 0.1,
      },
      columnStyles: {
        0: { halign: "center" },
        1: { halign: "left" },
        2: { halign: "right" },
        3: { halign: "right" },
        4: { halign: "right" },
        5: { halign: "right" },
      },
      startY: y,
      head: [
        [
          "S.No",
          "Account Name",
          "Opening Balance",
          "Total Income",
          "Total Expense",
          "Current Balance",
        ],
      ],
      body: [
        ...dayBookTransaction.map((item, index) => [
          index + 1,
          item.accountName,
          rupeeFormat(item.openingBalance),
          rupeeFormat(item.totalDebit),
          rupeeFormat(item.totalCredit),
          rupeeFormat(item.currentBalance),
        ]),
        [
          "",
          "TOTAL",
          rupeeFormat(totalOpeningBalance),
          rupeeFormat(totalDayDebit),
          rupeeFormat(totalDayCredit),
          rupeeFormat(totalCurrentBalance),
        ],
      ],
      didParseCell: function (data) {
        if (data.row.index === dayBookTransaction.length) {
          data.cell.styles.fontStyle = "bold";
        }
      },
    });

    y = (doc as any).lastAutoTable.finalY + 10;
    doc.setFontSize(12);
    doc.text("Transactions: ", 15, y - 3);
    // ================= TRANSACTION TABLE =================
    autoTable(doc, {
      theme: "grid",
      tableWidth: "auto",
      styles: {
        fontSize: 10,
        cellPadding: 2,
        overflow: "linebreak",
        textColor: [0, 0, 0],
        lineColor: 0,
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        fontSize: 10,
        lineColor: 0,
        lineWidth: 0.1,
      },
      columnStyles: {
        0: { halign: "center" },
        1: { halign: "left" },
        2: { halign: "left" },
        3: { halign: "right" },
        4: { halign: "right" },
      },
      startY: y,
      head: [["S.No", "Account Name", "Particulars", "Income", "Expense"]],
      body: [
        ...transactionData.map((item, index) => [
          index + 1,
          item.accountName,
          item.particulars,
          item.debitAmount ? rupeeFormat(item.debitAmount) : "",
          item.creditAmount ? rupeeFormat(item.creditAmount) : "",
        ]),
        [
          "",
          "",
          "TOTAL",
          rupeeFormat(totalPartDebit),
          rupeeFormat(totalPartCredit),
        ],
      ],
      didParseCell: function (data) {
        if (data.row.index === transactionData.length) {
          data.cell.styles.fontStyle = "bold";
        }
      },
    });

    doc.save("daybook-report.pdf");
  };
  useEffect(() => {
    if (dayBookTransaction.length > 0) {
      setAccountName(dayBookTransaction[0].accountName);
    }
  }, [dayBookTransaction]);

  useEffect(() => {
    api
      .get(`transaction/daybook/?from_date=${fromDate}`, config)
      .then((response) => {
        if (response.status === 200 && response.data) {
          const summary = response.data.summary || [];
          const transactions = response.data.transactions || [];

          setDayBookTransaction(summary);
          setTransactionData(
            transactions.map((item: any) => ({
              sourceId: item.source_id,
              date: item.date,
              accountName: item.account_name,
              accountNumber: item.account_number,
              debitAmount: item.debit_amount,
              creditAmount: item.credit_amount,
              particulars: item.particulars,
            })),
          );
        } else {
          setDayBookTransaction([]);
          setTransactionData([]);
        }
      })
      .catch(() => {
        setDayBookTransaction([]);
        setTransactionData([]);
      });
  }, [fromDate]);

  let balance = Number(openingBalance);

  useEffect(() => {
    dispatch(fetchDropDown());
    dispatch(fetchAccountHolderAction());
  }, []);

  return (
    <>
      <Main>
        <PageContainer title="Daybook">
          <Card title={"Daybook"}>
            <div className="card shadow-box-2 px-0 mx-0 mb-1 pt-1">
              <div className="card-body ps-3 py-0">
                <div className="container-fluid">
                  <div className="row">
                    {/* <div className="col-sm-6 col-md-4 col-lg-4">
                                <FormInput
                                    label='Date'
                                    labelClassName="required"
                                    value={date}
                                    onChange={handleDate}
                                    placeholder="Date"
                                    containerClass="mb-2"
                                    type="date"
                                />
                            </div> */}

                    <div className="col-sm-6 col-md-4 col-lg-3">
                      <FormInput
                        name=""
                        label=" Date"
                        labelClassName="required"
                        value={fromDate}
                        onChange={handleFromDate}
                        placeholder="From Date"
                        containerClass="mb-2"
                        type="date"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {dayBookTransaction.length > 0 ? (
              <div className="card">
                <div className="card-body">
                  <div className="table-wrapper">
                    <table className="table colored">
                      <thead>
                        <tr>
                          <th>S.No</th>
                          <th>Account Name</th>
                          <th>Opening Balance</th>
                          <th>Total Income</th>
                          <th>Total Expense</th>
                          <th>Current Balance</th>
                        </tr>
                      </thead>

                      <tbody>
                        {dayBookTransaction.map((item, index) => (
                          <tr key={index}>
                            <td>{index + 1}</td>
                            <td>{item.accountName}</td>
                            <td>{rupeeFormat(item.openingBalance)}</td>
                            <td>{rupeeFormat(item.totalDebit)}</td>
                            <td>{rupeeFormat(item.totalCredit)}</td>
                            <td>{rupeeFormat(item.currentBalance)}</td>
                          </tr>
                        ))}
                      </tbody>

                      <tfoot>
                        <tr>
                          <td colSpan={2}>
                            <b>Total</b>
                          </td>
                          <td>{rupeeFormat(totalOpening)}</td>
                          <td>{rupeeFormat(totalDebit)}</td>
                          <td>{rupeeFormat(totalCredit)}</td>
                          <td>{rupeeFormat(totalBalance)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center">
                <label>No records found</label>
              </div>
            )}
            {transactionData.length > 0 ? (
              <div className="card">
                <div className="card-body">
                  <div className="table-wrapper">
                    <table className="table colored">
                      <thead>
                        <tr>
                          <th>S.No</th>
                          <th>Account Name</th>
                          <th>Particulars</th>
                          <th>Income</th>
                          <th>Expense</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactionData.map((item, index) => (
                          <tr key={index}>
                            <td>{index + 1}</td>
                            <td>{item.accountName}</td>
                            <td>{item.particulars}</td>
                            <td>{item.debitAmount}</td>
                            <td>{item.creditAmount}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan={3}>
                            <b>Total</b>
                          </td>
                          <td>{rupeeFormat(totalPartDebit)}</td>
                          <td>{rupeeFormat(totalPartCredit)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center">
                <label>No records found</label>
              </div>
            )}
            <div className="my-2 text-end">
              <button className="btn btn-primary" onClick={handleDownloadPDF}>
                Download PDF
                <i className="fe-download"></i>
              </button>
            </div>
          </Card>
        </PageContainer>
      </Main>
    </>
  );
};

const DayBookTransactionBody = ({
  sno,
  date,
  particulars,
  accountName,
  income,
  expense,
  balance,
}: TableBody) => {
  return (
    <tr>
      <td>{sno}</td>
      <td className="text-truncate">{date}</td>
      {/* <td className='text-capitalize'>{accountName}</td> */}
      <td className="text-uppercase">{particulars}</td>
      <td className="text-end">
        {parseFloat(income) !== 0 ? rupeeFormat(income) : ""}
      </td>
      <td className="text-end">
        {parseFloat(expense) !== 0 ? rupeeFormat(expense) : ""}
      </td>
      <td className="text-end">{rupeeFormat(balance)}</td>
    </tr>
  );
};

export default DayBookReport;
